import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, OAuthProvider } from '../context/AuthContext';
import { authService } from '../services';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { brandHeroGradient } from '../theme/styleHelpers';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  Stack,
  InputAdornment,
  IconButton,
  FormHelperText,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  Google as GoogleIcon,
  Microsoft as MicrosoftIcon,
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useTheme } from '../context/ThemeContext';

const Register: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<OAuthProvider | null>(null);
  const [availableOAuthProviders, setAvailableOAuthProviders] = useState<OAuthProvider[]>([]);
  
  const { register, loginWithOAuth } = useAuth();
  const navigate = useNavigate();
  const { effectiveMode: mode } = useTheme();
  const muiTheme = useMuiTheme();

  // Fetch available OAuth providers on mount
  useEffect(() => {
    const fetchOAuthProviders = async () => {
      try {
        const providers = await authService.getAvailableOAuthProviders();
        setAvailableOAuthProviders(providers);
      } catch (error) {
        console.error('Failed to fetch OAuth providers:', error);
        // Silently fail - OAuth buttons will be hidden
      }
    };
    fetchOAuthProviders();
  }, []);

  // Password validation rules
  const validateLowercase = (password: string): boolean => /[a-z]/.test(password);
  const validateUppercase = (password: string): boolean => /[A-Z]/.test(password);
  const validateNumber = (password: string): boolean => /\d/.test(password);
  const validateSpecialChar = (password: string): boolean => /[@$!%*?&~]/.test(password);
  const validateLength = (password: string): boolean => password.length >= 8;

  const isPasswordValid = 
    validateLowercase(password) && 
    validateUppercase(password) && 
    validateNumber(password) && 
    validateSpecialChar(password) && 
    validateLength(password);

  const passwordStrength = [
    validateLength(password),
    validateLowercase(password),
    validateUppercase(password), 
    validateNumber(password),
    validateSpecialChar(password)
  ].filter(Boolean).length;

  const getPasswordStrengthColor = () => {
    if (passwordStrength <= 2) return 'error';
    if (passwordStrength <= 4) return 'warning';
    return 'success';
  };

  // Toggles for password visibility
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Age validation helper
  const calculateAge = (birthDate: string): number => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate date of birth (COPPA compliance - must be 13+)
    if (!dateOfBirth) {
      setError('Please enter your date of birth');
      return;
    }

    const age = calculateAge(dateOfBirth);
    if (age < 13) {
      setError('You must be at least 13 years old to create an account');
      return;
    }

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    if (!isPasswordValid) {
      setError(
        'Please ensure your password meets all the criteria'
      );
      return;
    }

    // Validate terms agreement
    if (!agreedToTerms) {
      setError('Please agree to the Terms of Service');
      return;
    }

    setLoading(true);

    try {
      await register(email, password, name, dateOfBirth);
      navigate('/dashboard');
    } catch (err: any) {
      // Extract detailed validation errors if available
      const validationErrors = err?.error?.details?.validation_errors || err?.details?.validation_errors;
      if (validationErrors && validationErrors.length > 0) {
        // Show the first validation error with field context
        const firstError = validationErrors[0];
        setError(`${firstError.field}: ${firstError.message}`);
      } else {
        setError(err.message || 'Failed to register');
      }
    } finally {
      setLoading(false);
    }
  };

  const passwordRequirements = [
    { label: 'At least 8 characters', valid: validateLength(password) },
    { label: 'One lowercase letter', valid: validateLowercase(password) },
    { label: 'One uppercase letter', valid: validateUppercase(password) },
    { label: 'One number', valid: validateNumber(password) },
    { label: 'One special character (@$!%*?&~)', valid: validateSpecialChar(password) },
  ];

  return (
    <Container component="main" maxWidth="sm" sx={{ mb: 4 }}>
      {/* Brand Logo/Header - Outside the dialog box */}
      <Box
        component={Link}
        to="/"
        sx={{
          mt: { xs: 4, sm: 6 },
          mb: 4,
          textDecoration: 'none',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          '&:hover': {
            opacity: 0.8
          }
        }}
      >
        <Typography
          variant="h3"
          component="div"
          sx={{
            fontWeight: 800,
            background: brandHeroGradient(muiTheme),
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 0.5
          }}
        >
          Tier One Content
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: '0.75rem' }}
        >
          Premium Content Sharing Platform
        </Typography>
      </Box>

      <Grid container justifyContent="center">
        <Grid xs={12}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              borderRadius: 2,
              bgcolor: mode === 'dark' ? 'background.paper' : 'white'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography variant="h4" component="h1" gutterBottom>
                Create an Account
              </Typography>

              <Typography variant="body2" color="text.secondary" align="center" mb={3}>
                Join the platform to start sharing content with your team
              </Typography>

              {error && (
                <Alert severity="error" sx={{ width: '100%', mb: 3 }}>
                  {error}
                </Alert>
              )}
              
              <Box component="form" onSubmit={handleRegister} noValidate sx={{ width: '100%' }}>
                <TextField
                  margin="normal"
                  fullWidth
                  id="name"
                  label="Full Name"
                  name="name"
                  autoComplete="name"
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="dateOfBirth"
                  label="Date of Birth"
                  name="dateOfBirth"
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    max: new Date().toISOString().split('T')[0], // Prevent future dates
                  }}
                  helperText="You must be at least 13 years old to use this service"
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onInput={(e: React.FormEvent<HTMLDivElement>) => {
                    const target = e.target as HTMLInputElement;
                    setPassword(target.value);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                
                {/* Password strength indicator */}
                {password && (
                  <Box sx={{ mt: 1, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Password Strength
                      </Typography>
                      <Typography variant="body2" color={getPasswordStrengthColor()}>
                        {passwordStrength <= 2 ? 'Weak' : passwordStrength <= 4 ? 'Medium' : 'Strong'}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={(passwordStrength / 5) * 100} 
                      color={getPasswordStrengthColor()}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                    
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Password must have:
                      </Typography>
                      <Grid container spacing={1}>
                        {passwordRequirements.map((req, index) => (
                          <Grid xs={12} sm={6} key={index}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {req.valid ? (
                                <CheckCircleIcon fontSize="small" color="success" sx={{ mr: 1 }} />
                              ) : (
                                <CancelIcon fontSize="small" color="error" sx={{ mr: 1 }} />
                              )}
                              <Typography 
                                variant="body2" 
                                color={req.valid ? 'success.main' : 'text.secondary'}
                              >
                                {req.label}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Box>
                )}
                
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={confirmPassword !== '' && password !== confirmPassword}
                  helperText={confirmPassword !== '' && password !== confirmPassword ? 'Passwords do not match' : ''}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={handleToggleConfirmPasswordVisibility}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                
                <Box sx={{ mt: 2 }}>
                  <Button
                    onClick={() => setAgreedToTerms(!agreedToTerms)}
                    variant={agreedToTerms ? "contained" : "outlined"}
                    size="small"
                    color={agreedToTerms ? "primary" : "inherit"}
                    sx={{ mr: 1 }}
                  >
                    {agreedToTerms ? "Agreed" : "Agree"}
                  </Button>
                  <Typography variant="body2" component="span">
                    to our{' '}
                    <Link
                      to="/legal/terms"
                      style={{
                        textDecoration: 'none',
                        color: muiTheme.palette.primary.main,
                        fontWeight: 600
                      }}
                    >
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link
                      to="/legal/privacy"
                      style={{
                        textDecoration: 'none',
                        color: muiTheme.palette.primary.main,
                        fontWeight: 600
                      }}
                    >
                      Privacy Policy
                    </Link>
                  </Typography>
                </Box>
                
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading || !email || !password || !confirmPassword || password !== confirmPassword || !isPasswordValid || !agreedToTerms}
                >
                  {loading ? <CircularProgress size={24} /> : 'Create Account'}
                </Button>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Typography variant="body2">
                    Already have an account?{' '}
                    <Link
                      to="/login"
                      style={{
                        textDecoration: 'none',
                        color: muiTheme.palette.primary.main,
                        fontWeight: 600
                      }}
                    >
                      Sign in
                    </Link>
                  </Typography>
                </Box>
                
                {availableOAuthProviders.length > 0 && (
                  <>
                    <Divider sx={{ my: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        OR
                      </Typography>
                    </Divider>

                    <Stack spacing={2}>
                      {availableOAuthProviders.includes('google') && (
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={oauthLoading === 'google' ? <CircularProgress size={20} /> : <GoogleIcon />}
                          onClick={() => {
                            setOauthLoading('google');
                            loginWithOAuth('google');
                          }}
                          disabled={!!oauthLoading}
                        >
                          Continue with Google
                        </Button>
                      )}

                      {availableOAuthProviders.includes('microsoft') && (
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={oauthLoading === 'microsoft' ? <CircularProgress size={20} /> : <MicrosoftIcon />}
                          onClick={() => {
                            setOauthLoading('microsoft');
                            loginWithOAuth('microsoft');
                          }}
                          disabled={!!oauthLoading}
                        >
                          Continue with Microsoft
                        </Button>
                      )}
                    </Stack>
                  </>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Register;